<script setup lang="ts">
import { ref, type UnwrapRef, watch } from 'vue'
import InputText from '@/components/input-text.vue'
import InputLabel from '@/components/input-label.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import InputCheckbox from '@/components/input-checkbox.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import { useQuery } from '@tanstack/vue-query'
import { checkAssetCreationEligibilityQueryOptions } from '@/features/assets/queries'
import { ValidationError } from '@/shared/api/validation'
import { useForm } from 'vee-validate'
import { faMagnifyingGlass, faTimes } from '@fortawesome/pro-regular-svg-icons'
import ButtonPrimary from '@/components/button-primary.vue'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

defineEmits<{ close: [] }>()

const projectCode = ref<number>()
const { values, setFieldError, handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      isEnergyProProject: z.boolean().default(false),
      unifiiProjectCode: z.coerce.number().int().gt(0),
      energyProProjectCode: z.number().int().gt(0).optional(),
    })
  ),
})
const performLookup = handleSubmit(
  values => {
    projectCode.value = values.unifiiProjectCode
  }
)
const { data, error, isFetching } = useQuery(checkAssetCreationEligibilityQueryOptions(projectCode))
const details = defineModel<UnwrapRef<typeof data>>('details')

watch(
  error,
  err => {
    if (err instanceof ValidationError) {
      const projectCodeError = err.errors.find(x => x.code?.toLowerCase() === 'project_not_found')
      const assetAlreadyExistsError = err.errors.find(x => x.code?.toLowerCase() === 'asset_already_exists')
      const errorMessage = projectCodeError?.message ?? assetAlreadyExistsError?.message

      if (errorMessage) {
        setFieldError('unifiiProjectCode', errorMessage)
      } else {
        setFieldError('unifiiProjectCode', 'Unable to verify whether this project code is valid.')
      }
    } else if (err) {
      setFieldError('unifiiProjectCode', 'Unable to verify whether this project code is valid.')
    } else {
      setFieldError('unifiiProjectCode', undefined)
    }
  }
)

watch(
  data,
  value => {
    if (value) {
      details.value = value
    }
  }
)
</script>

<template>
  <ModalTitle>New Asset</ModalTitle>
  <ModalContent>
    <div class="d-grid align-items-center" style="grid-template-columns: max-content 50%; gap: 13px">
      <InputLabel>Unifii Project Code</InputLabel>
      <InputText name="unifiiProjectCode" error-variant="tooltip" @change="details = undefined" />

      <!--<InputLabel>Is this an EnergyPro project?</InputLabel>
      <InputCheckbox name="isEnergyProProject" /> -->

      <template v-if="values.isEnergyProProject">
        <InputLabel>EnergyPro project code</InputLabel>
        <InputText name="energyProProjectCode" error-variant="tooltip" />
      </template>
    </div>
  </ModalContent>
  <ModalFooter class="text-end">
    <ButtonSecondary :disabled="isFetching" :icon="faTimes" @click="$emit('close')">Cancel</ButtonSecondary>
    <ButtonPrimary class="ms-2" :state="isFetching ? 'pending' : undefined" :icon="faMagnifyingGlass" @click="performLookup">Search</ButtonPrimary>
  </ModalFooter>
</template>

<style scoped lang="scss">

</style>
